import { type ReactNode } from 'react';
import { CalendarIcon, PinIcon, CheckIcon } from '@bravemove/design-tokens/icons';
import { Avatar } from '../Avatar/Avatar';
import { cn } from '../../utils/cn';

/**
 * F41 — Match card (web) — the bracket's atomic node.
 *
 * Renders one `MatchDetail` from the in-play bracket: a status header, two
 * participant rows (Avatar + seed chip + name + per-set score), and an optional
 * court/time footer. The winner row is emphasised (semibold + an accent left
 * rail + a check glyph) and the loser muted; an `in_progress` match shows a
 * pulsing accent dot, `bye`/`walkover` reads as a quiet italic auto-advance,
 * and an unfilled slot renders a dashed TBD placeholder.
 *
 * Engineering-authored, token-driven, composable.
 *
 * The read shapes below are structurally identical to `MatchDetail` /
 * `TournamentMatchStatus` / `MatchParticipantRef` from `@bravemove/types` —
 * mirrored locally (and not imported) to keep `@bravemove/ui-web` free of a runtime
 * workspace dep on `@bravemove/types` (the LifecycleChip / PaymentStatusChip
 * precedent). Values from `@bravemove/types`'s `MatchDetail` are assignable here.
 */

/** Match status — mirrors `TournamentMatchStatus`. */
export type TournamentMatchStatus = 'scheduled' | 'in_progress' | 'final' | 'bye' | 'walkover';

/** Mirrors `MatchParticipantRef`. */
export interface MatchParticipantRef {
  readonly registrationId: string;
  readonly displayName: string;
  readonly seed: number | null;
}

/** Mirrors `TournamentScore` (sport-aware per-set pairs). */
export interface MatchScore {
  readonly sets: ReadonlyArray<{ readonly a: number; readonly b: number }>;
  readonly winnerSide?: 'a' | 'b';
}

/** Structurally identical to `MatchDetail` from `@bravemove/types`. */
export interface MatchDetail {
  readonly id: string;
  readonly participantA: MatchParticipantRef | null;
  readonly participantB: MatchParticipantRef | null;
  readonly status: TournamentMatchStatus;
  readonly score: MatchScore | null;
  readonly winnerRegistrationId: string | null;
  readonly hostId: string | null;
  readonly courtOrLocation: string | null;
  readonly scheduledAt: string | null;
  readonly isThirdPlace: boolean;
  /**
   * Single-elimination progression wiring (mirrors `MatchDetail.nextMatchId` /
   * `nextMatchSlot`). Optional here so a caller may omit them; `BracketView`
   * uses them to centre each match between its actual feeders (handles byes).
   */
  readonly nextMatchId?: string | null;
  readonly nextMatchSlot?: 'a' | 'b' | null;
}

export interface MatchCardProps {
  readonly match: MatchDetail;
  /** Fired on click — typically opens the match detail / score-entry flyout. */
  readonly onPress?: (() => void) | undefined;
  /** Optional override for a resolved host name (the `hostId` is just an id). */
  readonly hostName?: string | undefined;
  /** Compact list-row presentation (denser; no footer chrome). */
  readonly compact?: boolean | undefined;
  readonly className?: string | undefined;
}

const STATUS_LABEL: Record<TournamentMatchStatus, string> = {
  scheduled: 'Scheduled',
  in_progress: 'Live',
  final: 'Final',
  bye: 'Bye',
  walkover: 'Walkover',
};

/** The score for one side across all sets, e.g. `6  4  7`. */
function sideScore(match: MatchDetail, side: 'a' | 'b'): string {
  if (!match.score) return '';
  return match.score.sets.map((s) => s[side]).join('  ');
}

/** A small status pill that sits in the card header. */
function StatusPip({ status }: { status: TournamentMatchStatus }) {
  if (status === 'in_progress') {
    return (
      <span className="inline-flex items-center gap-1 text-[9px] font-mono font-bold uppercase tracking-wider text-accent">
        <span className="relative inline-flex h-1.5 w-1.5">
          <span className="absolute inline-flex h-full w-full rounded-full bg-accent opacity-60 motion-safe:animate-ping" />
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-accent" />
        </span>
        {STATUS_LABEL.in_progress}
      </span>
    );
  }
  if (status === 'final') {
    return (
      <span className="text-[9px] font-mono font-bold uppercase tracking-wider text-success">
        {STATUS_LABEL.final}
      </span>
    );
  }
  if (status === 'bye' || status === 'walkover') {
    return (
      <span className="text-[9px] font-mono uppercase tracking-wider text-ink-3 italic">
        {STATUS_LABEL[status]}
      </span>
    );
  }
  return (
    <span className="text-[9px] font-mono uppercase tracking-wider text-ink-3">
      {STATUS_LABEL.scheduled}
    </span>
  );
}

interface ParticipantRowProps {
  readonly label: string;
  readonly seed: number | null;
  readonly score: string;
  readonly isWinner: boolean;
  readonly isDecided: boolean;
  readonly isTbd: boolean;
}

function ParticipantRow({ label, seed, score, isWinner, isDecided, isTbd }: ParticipantRowProps) {
  // A "loser" is the non-winner of an already-decided match (mute it).
  const isLoser = isDecided && !isWinner && !isTbd;
  return (
    <div
      className={cn(
        'relative flex items-center gap-2 rounded-md py-1.5 pe-2 ps-2.5',
        // Accent left rail traces the participant that advanced.
        isWinner && 'bg-accent-soft',
      )}
    >
      {isWinner ? (
        <span
          aria-hidden="true"
          className="absolute inset-y-1 start-0 w-[3px] rounded-full bg-accent"
        />
      ) : null}
      <Avatar name={isTbd ? undefined : label} size="sm" />
      <div className="flex min-w-0 flex-1 items-center gap-1.5">
        {seed !== null ? (
          <span
            className={cn(
              'shrink-0 rounded-sm px-1 font-mono text-[10px] tabular-nums leading-tight',
              isWinner ? 'bg-accent text-accent-ink' : 'bg-surface-2 text-ink-3',
            )}
          >
            {seed}
          </span>
        ) : null}
        <span
          className={cn(
            'truncate text-[13px]',
            isTbd
              ? 'text-ink-3 italic'
              : isWinner
                ? 'font-bold text-ink'
                : isLoser
                  ? 'text-ink-3'
                  : 'text-ink-2',
          )}
        >
          {isTbd ? 'TBD' : label}
        </span>
        {isWinner ? (
          <span aria-hidden="true" className="shrink-0 text-accent">
            <CheckIcon size={12} color="currentColor" />
          </span>
        ) : null}
      </div>
      {score ? (
        <span
          className={cn(
            'shrink-0 font-mono text-[13px] tabular-nums',
            isWinner ? 'font-bold text-ink' : isLoser ? 'text-ink-3' : 'text-ink-2',
          )}
        >
          {score}
        </span>
      ) : null}
    </div>
  );
}

export function MatchCard({ match, onPress, hostName, compact, className }: MatchCardProps) {
  const winnerId = match.winnerRegistrationId;
  const isDecided = match.status === 'final' || match.status === 'walkover' || !!winnerId;
  const isLive = match.status === 'in_progress';
  const aWins = !!winnerId && match.participantA?.registrationId === winnerId;
  const bWins = !!winnerId && match.participantB?.registrationId === winnerId;

  const footer: ReactNode =
    match.scheduledAt || match.courtOrLocation || hostName ? (
      <div className="mt-2 flex flex-wrap items-center gap-3 border-t border-hairline pt-2 text-[11px] text-ink-3">
        {match.scheduledAt ? (
          <span className="inline-flex items-center gap-1">
            <CalendarIcon size={11} color="currentColor" />
            <span className="font-mono">{match.scheduledAt}</span>
          </span>
        ) : null}
        {match.courtOrLocation || hostName ? (
          <span className="inline-flex min-w-0 items-center gap-1">
            <PinIcon size={11} color="currentColor" />
            <span className="truncate">{hostName ?? match.courtOrLocation}</span>
          </span>
        ) : null}
      </div>
    ) : null;

  return (
    <div
      role={onPress ? 'button' : undefined}
      tabIndex={onPress ? 0 : undefined}
      onClick={onPress}
      onKeyDown={
        onPress
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onPress();
              }
            }
          : undefined
      }
      className={cn(
        'relative overflow-hidden rounded-lg border bg-surface p-2',
        // A decided match gets a hairline accent frame so the live tree reads at a glance.
        isLive ? 'border-accent' : isDecided ? 'border-hairline-2' : 'border-hairline',
        compact ? 'min-w-[200px]' : 'min-w-[220px]',
        onPress &&
          'cursor-pointer transition-colors hover:border-accent hover:bg-surface-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent',
        className,
      )}
      aria-label={`${match.participantA?.displayName ?? 'TBD'} vs ${match.participantB?.displayName ?? 'TBD'}`}
    >
      <div className="mb-1 flex items-center justify-between gap-2">
        {match.isThirdPlace ? (
          <span className="text-[9px] font-mono uppercase tracking-wider text-tertiary">
            3rd place
          </span>
        ) : (
          <span aria-hidden="true" />
        )}
        <StatusPip status={match.status} />
      </div>

      <ParticipantRow
        label={match.participantA?.displayName ?? ''}
        seed={match.participantA?.seed ?? null}
        score={sideScore(match, 'a')}
        isWinner={aWins}
        isDecided={isDecided}
        isTbd={!match.participantA}
      />
      <ParticipantRow
        label={match.participantB?.displayName ?? ''}
        seed={match.participantB?.seed ?? null}
        score={sideScore(match, 'b')}
        isWinner={bWins}
        isDecided={isDecided}
        isTbd={!match.participantB}
      />

      {!compact ? footer : null}
    </div>
  );
}

export default MatchCard;
